import { memo, useRef, useEffect, useState, useCallback } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { useOverviewData } from './useOverviewData';

const AREAS = [
  { key: 'gender', label: 'Gender', color: '#8B5CF6' },
  { key: 'climate', label: 'Climate', color: '#006837' },
  { key: 'nutrition', label: 'Nutrition', color: '#F37021' },
  { key: 'biodiversity', label: 'Biodiversity', color: '#14B8A6' },
  { key: 'poverty', label: 'Poverty', color: '#007DBC' },
];

// Four layers — drawn outermost first so inner layers paint on top
const LAYERS = [
  { key: 'notProvided', label: 'Not Provided', fill: '#cbd5e1', fillOpacity: 0.15, stroke: '#cbd5e1', strokeOpacity: 0.4 },
  { key: 'notTargeted', label: 'Not Targeted', fill: '#94a3b8', fillOpacity: 0.25, stroke: '#94a3b8', strokeOpacity: 0.5 },
  { key: 'significant', label: 'Significant', fill: '#3B9B61', fillOpacity: 0.3, stroke: '#3B9B61', strokeOpacity: 0.6 },
  { key: 'principal', label: 'Principal', fill: '#006837', fillOpacity: 0.5, stroke: '#006837', strokeOpacity: 0.8 },
];

const WIDTH = 440;
const HEIGHT = 400;
const CENTER_X = WIDTH / 2;
const CENTER_Y = HEIGHT / 2;
const MAX_RADIUS = 145;
const RING_COUNT = 5;
const ANGLE_SLICE = (2 * Math.PI) / AREAS.length;

const NICE_STEPS = [1, 2, 3, 5, 8, 10, 15, 20, 30, 40, 50, 75, 100, 125, 150, 200, 300, 400, 500, 750, 1000, 1500, 2000, 3000, 5000];

function polarX(angle, radius) {
  return CENTER_X + radius * Math.cos(angle - Math.PI / 2);
}
function polarY(angle, radius) {
  return CENTER_Y + radius * Math.sin(angle - Math.PI / 2);
}

export const ImpactAreaRadarChart = memo(function ImpactAreaRadarChart() {
  const { byImpactArea, stats, filteredResults } = useOverviewData();
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Compute raw counts per area
  const rawData = AREAS.map((area, i) => {
    const counts = byImpactArea?.[area.key] || { '-1': 0, 0: 0, 1: 0, 2: 0 };
    const principal = counts[2] || 0;
    const significant = counts[1] || 0;
    const notTargeted = counts[0] || 0;
    const notProvided = counts[-1] || 0;
    const total = notTargeted + significant + principal + notProvided;
    return { ...area, principal, significant, notTargeted, notProvided, total, angle: i * ANGLE_SLICE };
  });

  // Find max count across all (area × level) for normalization
  const maxCount = Math.max(
    ...rawData.map(d => d.notProvided),
    ...rawData.map(d => d.notTargeted),
    ...rawData.map(d => d.significant),
    ...rawData.map(d => d.principal),
    1,
  );

  // Auto-fit: pick tightest nice step
  const rawStep = maxCount / RING_COUNT;
  const autoRingStep = NICE_STEPS.find(s => s >= rawStep) || Math.ceil(rawStep / 1000) * 1000;
  const autoRingMax = autoRingStep * RING_COUNT;

  const ringMax = autoRingMax;
  const rings = Array.from({ length: RING_COUNT }, (_, i) => (i + 1) / RING_COUNT);

  // Normalize a count to 0..1 against ringMax
  const norm = (count) => Math.min(count / ringMax, 1);

  // Build polygon path
  const buildPath = (getCount) => {
    const points = rawData.map((d) => {
      const r = norm(getCount(d)) * MAX_RADIUS;
      return `${polarX(d.angle, r)},${polarY(d.angle, r)}`;
    });
    return `M${points.join('L')}Z`;
  };

  const paths = {
    notProvided: buildPath(d => d.notProvided),
    notTargeted: buildPath(d => d.notTargeted),
    significant: buildPath(d => d.significant),
    principal: buildPath(d => d.principal),
  };

  // Animate on mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleMouseMove = useCallback((d, e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 12,
      area: d,
    });
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  if (!byImpactArea || !stats) return null;

  // Ring label values
  const ringLabels = rings.map((_, i) => {
    const val = ringMax * (i + 1) / RING_COUNT;
    return val >= 10 ? Math.round(val).toLocaleString() : val.toFixed(1);
  });

  const chartData = rawData.map(d => ({
    'Impact Area': d.label,
    'Principal': d.principal,
    'Significant': d.significant,
    'Not Targeted': d.notTargeted,
    'Not Provided': d.notProvided,
    'Total': d.total,
  }));

  return (
    <ChartWrapper
      title="Impact Area Targeting"
      info="Radar shows result counts for each targeting level across CGIAR's 5 impact areas. Each polygon represents one level: Not Targeted, Significant, or Principal. Hover each axis for details."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="impact-area-radar"
    >
      {/* Expand toggle */}
      <div className="flex items-center justify-end mb-1">
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-1 px-2 h-6 rounded border border-[var(--border-primary,#d1d5db)] text-[var(--text-tertiary,#9ca3af)] hover:bg-[var(--surface-hover,#f1f5f9)] text-[10px] leading-none"
          title={expanded ? 'Collapse chart' : 'Expand chart'}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {expanded ? (
              <>
                <polyline points="4 14 1 14 1 11" /><polyline points="12 2 15 2 15 5" />
                <line x1="14" y1="2" x2="9" y2="7" /><line x1="2" y1="14" x2="7" y2="9" />
              </>
            ) : (
              <>
                <polyline points="14 10 14 14 10 14" /><polyline points="2 6 2 2 6 2" />
                <line x1="14" y1="14" x2="9" y2="9" /><line x1="2" y1="2" x2="7" y2="7" />
              </>
            )}
          </svg>
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      <div ref={containerRef} className="relative flex justify-center">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className={expanded ? 'w-full' : 'w-full max-w-[460px]'}
          style={{ overflow: 'visible', transition: 'max-width 0.3s ease' }}
        >

          {/* Background fill for grid area */}
          <circle
            cx={CENTER_X}
            cy={CENTER_Y}
            r={MAX_RADIUS}
            fill="var(--surface-secondary, #f8fafc)"
          />

          {/* Grid rings */}
          {rings.map((pct, i) => (
            <g key={i}>
              <circle
                cx={CENTER_X}
                cy={CENTER_Y}
                r={MAX_RADIUS * pct}
                fill="none"
                stroke="var(--border-primary, #d1d5db)"
                strokeWidth={pct === 1 ? '1' : '0.5'}
                strokeDasharray={pct < 1 ? '4,3' : 'none'}
              />
              <text
                x={CENTER_X + 5}
                y={CENTER_Y - MAX_RADIUS * pct + 13}
                fontSize="9"
                fill="var(--text-tertiary, #94a3b8)"
              >
                {ringLabels[i]}
              </text>
            </g>
          ))}

          {/* Axis lines */}
          {rawData.map((d) => (
            <line
              key={d.key}
              x1={CENTER_X}
              y1={CENTER_Y}
              x2={polarX(d.angle, MAX_RADIUS)}
              y2={polarY(d.angle, MAX_RADIUS)}
              stroke="var(--border-primary, #d1d5db)"
              strokeWidth="0.75"
            />
          ))}

          {/* Four polygons — outermost first */}
          {LAYERS.map((layer, li) => (
            <path
              key={layer.key}
              d={paths[layer.key]}
              fill={layer.fill}
              fillOpacity={layer.fillOpacity}
              stroke={layer.stroke}
              strokeWidth={li === 3 ? 2 : 1.5}
              strokeOpacity={layer.strokeOpacity}
              strokeLinejoin="round"
              style={{
                transform: animated ? 'scale(1)' : 'scale(0)',
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
                transition: `transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${li * 100}ms`,
              }}
            />
          ))}

          {/* Axis dots and labels */}
          {rawData.map((d) => {
            const labelR = MAX_RADIUS + 26;
            return (
              <g key={d.key}>
                {/* Interactive hit area along the axis */}
                <line
                  x1={CENTER_X}
                  y1={CENTER_Y}
                  x2={polarX(d.angle, MAX_RADIUS)}
                  y2={polarY(d.angle, MAX_RADIUS)}
                  stroke="transparent"
                  strokeWidth="28"
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => handleMouseMove(d, e)}
                  onMouseLeave={handleMouseLeave}
                />
                {/* Dots on each layer edge — clamped to grid */}
                {LAYERS.map((layer) => {
                  const r = Math.min(norm(d[layer.key]) * MAX_RADIUS, MAX_RADIUS);
                  return (
                    <circle
                      key={layer.key}
                      cx={polarX(d.angle, r)}
                      cy={polarY(d.angle, r)}
                      r="3"
                      fill={layer.stroke}
                      stroke="white"
                      strokeWidth="1.5"
                      style={{
                        opacity: animated ? 1 : 0,
                        transition: 'opacity 0.4s ease 0.7s',
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })}
                {/* Label */}
                <text
                  x={polarX(d.angle, labelR)}
                  y={polarY(d.angle, labelR)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill={d.color}
                  style={{ pointerEvents: 'none' }}
                >
                  {d.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg z-50"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="font-semibold mb-1" style={{ color: tooltip.area.color }}>{tooltip.area.label}</div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#006837' }} />
              Principal: {tooltip.area.principal.toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#3B9B61' }} />
              Significant: {tooltip.area.significant.toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#94a3b8' }} />
              Not Targeted: {tooltip.area.notTargeted.toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#cbd5e1' }} />
              Not Provided: {tooltip.area.notProvided.toLocaleString()}
            </div>
            <div className="mt-1 pt-1 border-t border-gray-700 text-gray-400">Total: {tooltip.area.total.toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-5 mt-3">
        {[...LAYERS].reverse().map((layer) => (
          <div key={layer.key} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm border"
              style={{
                backgroundColor: layer.fill,
                opacity: layer.fillOpacity + 0.2,
                borderColor: layer.stroke,
              }}
            />
            <span className="text-xs text-[var(--text-secondary)]">{layer.label}</span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});
